import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParentDashboard, useEloHistory } from '@/api/dashboard';
import type {
  ParentDashboardResponse,
  EloPoint as ApiEloPoint,
  TrendKind,
} from '@/api/dashboard';
import {
  Caption,
  Card,
  Button,
  Divider,
  StatStrip,
  AnimatedNumber,
  Heatmap,
  PulseDot,
  ModuleTile,
  CadencePanel,
  SessionsPanel,
  ELOCurveCard,
  SessionDetailModal,
  PhaseDetailModal,
  TACTICS_THEME_LABEL_FR,
  type CadenceRow,
  type SessionRow,
  type EloPoint,
  type SessionDetail,
  type PhaseDetail,
} from '@/ui';
import { parentApi } from './api';

/**
 * Parent Dashboard V2 — port proto §ParentDashboard (~600 LOC visibles +
 * sous-composants).
 *
 * 9 panels (cf. WAKEUP_MVP2_T2_FRONTEND_PORT.md §3.5) :
 *  1. Period switcher 7/30/90j
 *  2. Toggle compare vs préc.
 *  3. 6 KPIs avec deltas (attempts, success_rate, sessions, time_min, cards
 *     backlog, faiblesses top 1)
 *  4. Activity heatmap 90j (shared Heatmap)
 *  5. Modules panel (4 tiles via ModuleTile shared)
 *  6. Phases panel (3 bars cliquables → PhaseDetailModal)
 *  7. Weaknesses panel (top 5 tactics)
 *  8. CadencePanel + SessionsPanel (shared) + SessionDetailModal
 *  9. Alerts panel (warn / good / info)
 *
 * Wiring : `useParentDashboard(period, compare)` (hook fusion #49) →
 * `/api/v1/parent/dashboard?period=&compare=previous` (backend #48 OK).
 * + `useEloHistory()` pour ELOCurveCard.
 *
 * Single source of truth : SessionDetailModal et PhaseDetailModal viennent
 * de `@/ui/modals` — partagés avec Mission Control, jamais dupliqués.
 */

/**
 * Source unique des labels FR des thèmes Lichess : `TACTICS_THEME_LABEL_FR`
 * exporté par `@/ui/labels.ts`.
 */

type Period = 7 | 30 | 90;

export function ParentDashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(30);
  const [compare, setCompare] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionDetail | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<PhaseDetail | null>(null);

  const dashQ = useParentDashboard(period, compare);
  const eloQ = useEloHistory();

  async function handleLogout(): Promise<void> {
    try {
      await parentApi.logout();
    } catch {
      // best-effort
    }
    navigate('/parent', { replace: true });
  }

  if (dashQ.isError) {
    return (
      <main className="mx-auto max-w-md px-6 py-16 flex flex-col gap-6">
        <p className="text-body text-coral-500">Session parent expirée ou invalide.</p>
        <button
          type="button"
          onClick={() => navigate('/parent', { replace: true })}
          className="rounded-lg border border-paper-50 px-5 py-3 text-center text-body font-display text-paper-50 hover:bg-paper-50 hover:text-ink-900 transition-all duration-150"
        >
          Re-saisir le PIN
        </button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
      {/* Header — caption + h1 + period switcher + compare toggle + logout */}
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--paper-50)" size={7} />
            <Caption color="paper">Espace parent · lecture seule</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">KPIs de Léon</h1>
          <p className="text-body-sm text-ink-300">
            Snapshot agrégé sur {periodLabel(period)}. La courbe ELO vient des points
            enregistrés après chaque tournoi.
          </p>
        </div>
        <div className="flex flex-col gap-2.5 items-end">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setCompare((v) => !v)}
              className={`px-3 py-1.5 rounded-lg border font-display text-xs inline-flex items-center gap-1.5 transition-all duration-150 ${
                compare
                  ? 'border-paper-50 bg-paper-50/10 text-paper-50'
                  : 'border-ink-700 bg-ink-800 text-ink-300 hover:border-ink-600 hover:text-paper-50'
              }`}
            >
              ◫ Comparer vs préc.
            </button>
            <div className="inline-flex bg-ink-800 border border-ink-700 rounded-lg overflow-hidden">
              {([7, 30, 90] as Period[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 font-mono text-xs transition-all duration-150 ${
                    period === p
                      ? 'bg-paper-50 text-ink-900'
                      : 'text-ink-300 hover:text-paper-50'
                  }`}
                >
                  {p}j
                </button>
              ))}
            </div>
          </div>
          <Button variant="ghost" size="compact" onClick={handleLogout}>
            Quitter
          </Button>
        </div>
      </header>

      {dashQ.isPending && (
        <Card>
          <p className="text-ink-300 italic">Chargement du snapshot…</p>
        </Card>
      )}

      {dashQ.data && (
        <>
          {/* Panel 3 — KPI strip with deltas */}
          <KpiStrip data={dashQ.data} />

          {/* Panel ELO trajectory */}
          <EloSection eloHistory={eloQ.data} />

          {/* Panel 4 — Activity heatmap + Alerts side by side */}
          <section className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
            <HeatmapPanel data={dashQ.data} period={period} />
            <AlertsPanel alerts={dashQ.data.alerts} />
          </section>

          {/* Panel 5 — Modules + Panel 6 — Phases side by side */}
          <section className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
            <ModulesPanel data={dashQ.data} onNavigate={navigate} />
            <PhasesPanel data={dashQ.data} onSelectPhase={setSelectedPhase} />
          </section>

          {/* Panel 7 — Weaknesses + Cards backlog */}
          <section className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
            <WeaknessesPanel weaknesses={dashQ.data.weaknesses_top5} />
            <BacklogPanel data={dashQ.data} onOpenRevision={() => navigate('/revision-cards')} />
          </section>

          {/* Panel 7bis — MVP2.4 T9.2 — KPI central recurring_mistake_rate */}
          {dashQ.data.recurring_mistake_rate && (
            <RecurringMistakesPanel
              rate={dashQ.data.recurring_mistake_rate}
              periodDays={dashQ.data.period_days}
            />
          )}

          {/* Panel 8 — Cadence + Sessions */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CadencePanel
              cadence={dashQ.data.cadence as CadenceRow[]}
              totalGames={dashQ.data.cadence.reduce((sum, c) => sum + c.games, 0)}
            />
            <SessionsPanel
              sessions={dashQ.data.sessions as SessionRow[]}
              limit={5}
              onSelect={(s) => setSelectedSession(toSessionDetail(s))}
            />
          </section>
        </>
      )}

      <SessionDetailModal session={selectedSession} onClose={() => setSelectedSession(null)} />
      <PhaseDetailModal phase={selectedPhase} onClose={() => setSelectedPhase(null)} />

      <footer className="pt-4 text-center">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
        >
          ← Côté Léon
        </button>
      </footer>
    </main>
  );
}

// ─── Panels ────────────────────────────────────────────────────────────────

function KpiStrip({ data }: { data: ParentDashboardResponse }): JSX.Element {
  const t = data.totals;
  const d = data.deltas;
  return (
    <StatStrip className="flex-wrap items-stretch gap-x-6 gap-y-4">
      <KpiTile
        label="Tentatives"
        value={t.attempts}
        {...(d?.attempts !== undefined ? { delta: d.attempts } : {})}
      />
      <Divider vertical />
      <KpiTile
        label="Réussite"
        value={t.success_rate}
        format={(v) => `${Math.round(v)}%`}
        {...(d?.success_rate !== undefined ? { delta: d.success_rate } : {})}
        color="var(--volt-500)"
      />
      <Divider vertical />
      <KpiTile
        label="Sessions"
        value={t.sessions}
        {...(d?.sessions !== undefined ? { delta: d.sessions } : {})}
      />
      <Divider vertical />
      <KpiTile
        label="Temps"
        value={t.time_min}
        format={(v) => `${Math.round(v)} min`}
        {...(d?.time_min !== undefined ? { delta: d.time_min } : {})}
      />
      <Divider vertical />
      <div className="flex flex-col gap-1 min-w-[160px]">
        <Caption>Cartes</Caption>
        <span className="font-mono text-sm leading-snug">
          <span className="text-paper-50 font-medium">{data.cards_backlog}</span>
          <span className="text-ink-300"> en attente · </span>
          <span className="text-acid-500 font-medium">{data.cards_due_today}</span>
          <span className="text-ink-300"> dues</span>
        </span>
      </div>
    </StatStrip>
  );
}

function KpiTile({
  label,
  value,
  format = (v) => String(Math.round(v)),
  delta,
  color = 'var(--paper-50)',
}: {
  label: string;
  value: number;
  format?: (v: number) => string;
  delta?: number;
  color?: string;
}): JSX.Element {
  const deltaSign = delta !== undefined && delta > 0 ? '+' : '';
  const deltaColor =
    delta === undefined
      ? 'text-ink-300'
      : delta > 0
        ? 'text-volt-500'
        : delta < 0
          ? 'text-coral-500'
          : 'text-ink-300';
  return (
    <div className="flex flex-col gap-1 min-w-[100px]">
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        format={format}
        className="font-mono"
        style={{ fontSize: 24, color, lineHeight: 1, fontWeight: 500 }}
      />
      {delta !== undefined && (
        <span className={`font-mono text-[11px] ${deltaColor}`}>
          {deltaSign}
          {Math.round(delta)} vs préc.
        </span>
      )}
    </div>
  );
}

function HeatmapPanel({
  data,
  period,
}: {
  data: ParentDashboardResponse;
  period: Period;
}): JSX.Element {
  const rows = data.regularity ?? [];
  const totalDays = period;
  const days = buildHeatmapDays(rows, totalDays);
  const activeDays = rows.filter((r) => r.attempts > 0).length;
  const totalAttempts = rows.reduce((sum, r) => sum + r.attempts, 0);
  return (
    <Card>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <Caption>Activité · {periodLabel(period)}</Caption>
        <span className="font-mono text-[11px] text-ink-300">
          <span className="text-paper-50">{activeDays}</span> jours actifs ·{' '}
          <span className="text-paper-50">{totalAttempts}</span> tentatives
        </span>
      </div>
      <Heatmap data={days} cell={11} gap={3} weeks={Math.ceil(totalDays / 7)} color="var(--volt-500)" />
      <div className="flex items-center gap-3 mt-3 text-[10px] text-ink-300 font-mono uppercase tracking-wider">
        <span>moins</span>
        {[0, 1, 2, 3, 4].map((level) => (
          <span
            key={level}
            className="w-2.5 h-2.5 rounded-sm"
            style={{
              background: level === 0 ? 'var(--ink-700)' : 'var(--volt-500)',
              opacity: level === 0 ? 1 : 0.08 + (level / 4) * 0.92,
            }}
          />
        ))}
        <span>plus</span>
      </div>
    </Card>
  );
}

function AlertsPanel({
  alerts,
}: {
  alerts: ParentDashboardResponse['alerts'];
}): JSX.Element {
  if (!alerts || alerts.length === 0) {
    return (
      <Card>
        <Caption>Alertes</Caption>
        <p className="mt-2 text-sm text-ink-300 italic">Aucune alerte sur cette période.</p>
      </Card>
    );
  }
  return (
    <Card>
      <Caption>Alertes</Caption>
      <ul className="mt-3 flex flex-col gap-2 list-none p-0">
        {alerts.map((a, i) => {
          const color =
            a.kind === 'warn'
              ? 'var(--amber-500)'
              : a.kind === 'good'
                ? 'var(--volt-500)'
                : 'var(--paper-50)';
          const icon = a.kind === 'warn' ? '⚠' : a.kind === 'good' ? '✓' : 'ℹ';
          return (
            <li
              key={i}
              className="flex items-start gap-2.5 px-3 py-2 rounded-lg border"
              style={{
                borderColor: `${color}33`,
                backgroundColor: `${color}08`,
              }}
            >
              <span
                aria-hidden="true"
                className="font-mono text-xs"
                style={{ color }}
              >
                {icon}
              </span>
              <span className="flex-1 text-sm text-paper-50 leading-snug">{a.text}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function ModulesPanel({
  data,
  onNavigate,
}: {
  data: ParentDashboardResponse;
  onNavigate: (path: string) => void;
}): JSX.Element {
  const m = data.modules;
  const tiles = [
    { id: 'opening' as const, label: 'Opening Lab', tagline: 'Armes sales.', color: 'var(--violet-500)', stats: m.opening, path: '/opening-lab' },
    { id: 'tactic' as const, label: 'Tactics Dojo', tagline: 'Vois le coup.', color: 'var(--cyan-500)', stats: m.tactic, path: '/tactics-dojo' },
    { id: 'autopsy' as const, label: 'Game Autopsy', tagline: "C'est un dataset.", color: 'var(--coral-500)', stats: m.autopsy, path: '/game-autopsy' },
    { id: 'endgame' as const, label: 'Endgame Forge', tagline: 'Conversion.', color: 'var(--amber-500)', stats: m.endgame, path: '/endgame-forge' },
  ];
  return (
    <Card>
      <Caption>Modules · {data.period_days} derniers jours</Caption>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tiles.map((t) => (
          <ModuleTile
            key={t.id}
            label={t.label}
            tagline={t.tagline}
            color={t.color}
            progress={t.stats.mastery_pct}
            footer={`${t.stats.attempts} tentatives`}
            onClick={() => onNavigate(t.path)}
          />
        ))}
      </div>
    </Card>
  );
}

function PhasesPanel({
  data,
  onSelectPhase,
}: {
  data: ParentDashboardResponse;
  onSelectPhase: (phase: PhaseDetail) => void;
}): JSX.Element {
  const p = data.phases;
  const total = p.opening + p.middlegame + p.endgame;
  const phases: Array<{ key: 'opening' | 'middlegame' | 'endgame'; label: string; color: string; count: number }> = [
    { key: 'opening', label: 'Ouverture', color: 'var(--violet-500)', count: p.opening },
    { key: 'middlegame', label: 'Milieu', color: 'var(--cyan-500)', count: p.middlegame },
    { key: 'endgame', label: 'Finale', color: 'var(--amber-500)', count: p.endgame },
  ];
  return (
    <Card>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <Caption>Phases · {data.period_days} jours</Caption>
        <span className="font-mono text-[11px] text-ink-300">
          {total} erreurs · cliquer pour détail
        </span>
      </div>
      <PhaseDonut phases={phases} total={total} />
      <ul className="mt-4 flex flex-col gap-1.5 list-none p-0">
        {phases.map((ph) => {
          const pct = total > 0 ? Math.round((ph.count / total) * 100) : 0;
          return (
            <li key={ph.key}>
              <button
                type="button"
                onClick={() => onSelectPhase({ phase: ph.key, total: ph.count })}
                className="w-full text-left px-3 py-2 rounded-md border border-ink-700 bg-ink-900 hover:bg-ink-700/40 transition-all duration-150 flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="size-2 rounded-full"
                    style={{ background: ph.color }}
                  />
                  <span className="text-sm text-paper-50">{ph.label}</span>
                </span>
                <span className="font-mono text-xs text-ink-300 tabular-nums">
                  <span className="text-paper-50">{ph.count}</span> · {pct}%
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function PhaseDonut({
  phases,
  total,
}: {
  phases: Array<{ key: string; color: string; count: number }>;
  total: number;
}): JSX.Element {
  const size = 140;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const segments = phases.map((ph) => {
    const pct = total > 0 ? ph.count / total : 0;
    const dash = pct * c;
    const seg = { color: ph.color, dash, offset, key: ph.key };
    offset += dash;
    return seg;
  });
  return (
    <div className="flex justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="var(--ink-700)"
          strokeWidth={stroke}
          fill="none"
        />
        {segments.map((s) => (
          <circle
            key={s.key}
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={s.color}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={`${s.dash} ${c}`}
            strokeDashoffset={-s.offset}
            style={{ transition: 'stroke-dasharray 200ms ease-out' }}
          />
        ))}
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="rotate-90 origin-center"
          fontFamily="JetBrains Mono"
          fontSize="22"
          fill="var(--paper-50)"
          fontWeight="500"
          transform={`rotate(90 ${size / 2} ${size / 2})`}
        >
          {total}
        </text>
      </svg>
    </div>
  );
}

function WeaknessesPanel({
  weaknesses,
}: {
  weaknesses: ParentDashboardResponse['weaknesses_top5'];
}): JSX.Element {
  return (
    <Card>
      <Caption>Faiblesses tactiques · top 5</Caption>
      {weaknesses.length === 0 ? (
        <p className="mt-3 text-body-sm text-ink-300 italic">
          Pas encore de données. Joue 10+ puzzles dans Tactics Dojo.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2 list-none p-0">
          {weaknesses.map((w) => {
            const max = Math.max(...weaknesses.map((x) => x.fails));
            const pct = max > 0 ? (w.fails / max) * 100 : 0;
            return (
              <li key={w.theme} className="flex items-center gap-3">
                <span className="text-sm text-paper-50 w-40 flex-none truncate">
                  {TACTICS_THEME_LABEL_FR[w.theme] ?? w.theme}
                </span>
                <div className="flex-1 h-1.5 bg-ink-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-coral-500 transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {/* T9.4 — chevron trend si le backend l'a annoté */}
                {w.trend && <TrendChevron trend={w.trend} />}
                <span className="font-mono text-xs text-coral-500 tabular-nums w-16 text-right">
                  {w.fails} échecs
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function TrendChevron({ trend }: { trend: TrendKind }): JSX.Element {
  const meta: Record<TrendKind, { icon: string; color: string; label: string }> = {
    improving: { icon: '↘', color: 'var(--volt-500)', label: 'En baisse — Léon progresse' },
    declining: { icon: '↗', color: 'var(--coral-500)', label: 'En hausse — vigilance' },
    stable: { icon: '→', color: 'var(--ink-300)', label: 'Stable' },
    insufficient_data: { icon: '·', color: 'var(--ink-300)', label: 'Données insuffisantes' },
  };
  const m = meta[trend];
  return (
    <span
      title={m.label}
      aria-label={m.label}
      className="font-mono text-xs tabular-nums w-5 text-center"
      style={{ color: m.color }}
    >
      {m.icon}
    </span>
  );
}

function RecurringMistakesPanel({
  rate,
  periodDays,
}: {
  rate: NonNullable<ParentDashboardResponse['recurring_mistake_rate']>;
  periodDays: number;
}): JSX.Element {
  const color =
    rate.rate >= 50
      ? 'var(--coral-500)'
      : rate.rate >= 25
        ? 'var(--amber-500)'
        : 'var(--volt-500)';
  return (
    <Card>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <Caption>Erreurs récurrentes · {periodDays} derniers jours</Caption>
        <span className="font-mono text-[11px] text-ink-300">
          KPI central de la couche apprentissage
        </span>
      </div>
      {rate.total === 0 ? (
        <p className="text-body-sm text-ink-300 italic">
          Pas encore d'erreurs analysées sur la fenêtre. Joue 5+ parties pour produire des
          autopsies.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-3 flex-wrap">
            <AnimatedNumber
              value={rate.rate}
              format={(v) => `${Math.round(v)}%`}
              className="font-mono"
              style={{ fontSize: 40, color, lineHeight: 1, fontWeight: 500 }}
            />
            <span className="text-sm text-ink-300">
              <span className="text-paper-50 font-medium">{rate.recurring}</span> /{' '}
              <span className="text-paper-50 font-medium">{rate.total}</span> erreurs partagent
              un motif avec une autre.
            </span>
          </div>
          {rate.top_recurring.length > 0 && (
            <div>
              <Caption>Motifs récurrents</Caption>
              <ul className="mt-2 flex flex-wrap gap-1.5 list-none p-0">
                {rate.top_recurring.map((tag) => (
                  <li
                    key={tag.tag}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-ink-700 bg-ink-900 font-mono text-[11px]"
                  >
                    <span className="text-paper-50">{tag.tag}</span>
                    <span className="text-coral-500">×{tag.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-ink-300 italic leading-snug">
            Si ce nombre baisse semaine après semaine, la console fait son travail :
            les erreurs déjà diagnostiquées disparaissent.
          </p>
        </div>
      )}
    </Card>
  );
}

function BacklogPanel({
  data,
  onOpenRevision,
}: {
  data: ParentDashboardResponse;
  onOpenRevision: () => void;
}): JSX.Element {
  const overSoftLimit = data.cards_backlog > 50;
  return (
    <Card hoverable onClick={onOpenRevision}>
      <Caption>Cartes de révision</Caption>
      <div className="mt-3 flex items-baseline gap-2">
        <AnimatedNumber
          value={data.cards_backlog}
          className="font-mono"
          style={{ fontSize: 40, color: 'var(--paper-50)', lineHeight: 1, fontWeight: 500 }}
        />
        <span className="text-sm text-ink-300">en attente</span>
      </div>
      <p className="mt-2 text-body-sm text-ink-300">
        <span className="text-acid-500 font-medium">{data.cards_due_today}</span> dues aujourd'hui.
      </p>
      {overSoftLimit && (
        <p className="mt-3 px-3 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs text-amber-500 leading-snug">
          ⚠ Au-delà de 50 cartes en attente. Suggère une session de rattrapage à Léon.
        </p>
      )}
      <p className="mt-3 text-caption uppercase text-ink-300 tracking-wider">
        Ouvrir la queue de révision →
      </p>
    </Card>
  );
}

function EloSection({
  eloHistory,
}: {
  eloHistory: { series: Record<string, ApiEloPoint[]>; kinds: string[] } | undefined;
}): JSX.Element {
  const standard = useMemo<EloPoint[]>(() => {
    if (!eloHistory) return [];
    const arr = eloHistory.series.fide_standard ?? eloHistory.series.standard ?? [];
    return arr.map((p) => ({ v: p.v, label: p.label, recorded_at: p.recorded_at }));
  }, [eloHistory]);
  const rapid = useMemo<EloPoint[]>(() => {
    if (!eloHistory) return [];
    const arr = eloHistory.series.fide_rapid ?? eloHistory.series.rapid ?? [];
    return arr.map((p) => ({ v: p.v, label: p.label, recorded_at: p.recorded_at }));
  }, [eloHistory]);
  return (
    <ELOCurveCard
      title="Trajectoire FIDE · Standard + Rapid"
      standard={standard}
      rapid={rapid}
      height={220}
      milestones
    />
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function periodLabel(p: Period): string {
  return p === 7 ? '7 jours' : p === 30 ? '30 jours' : '90 jours';
}

function buildHeatmapDays(
  rows: Array<{ day: string; attempts: number }>,
  totalDays: number,
): number[] {
  const map = new Map(rows.map((r) => [r.day, r.attempts]));
  const today = new Date();
  const out: number[] = [];
  for (let i = totalDays - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const count = map.get(key) ?? 0;
    let level = 0;
    if (count >= 16) level = 4;
    else if (count >= 9) level = 3;
    else if (count >= 4) level = 2;
    else if (count >= 1) level = 1;
    out.push(level);
  }
  return out;
}

function toSessionDetail(s: SessionRow): SessionDetail {
  return {
    id: s.id,
    date: s.date,
    start_time: s.start_time,
    duration_min: s.duration_min,
    attempts: s.attempts,
    success: s.success,
    modules: [...s.modules],
    avg_sec: s.avg_sec,
  };
}
